import { CoinsHandIcon } from '@/components/icons/CoinsHandIcon';
import { CurrencyDollarCircleIcon } from '@/components/icons/CurrencyDollarCircleIcon';
import { EdgeBothIcon } from '@/components/icons/EdgeBothIcon';
import { EdgeDashedIcon } from '@/components/icons/EdgeDashedIcon';
import { EdgeDottedIcon } from '@/components/icons/EdgeDottedIcon';
import { EdgeEndIcon } from '@/components/icons/EdgeEndIcon';
import { EdgeNoneIcon } from '@/components/icons/EdgeNoneIcon';
import { EdgeSolidIcon } from '@/components/icons/EdgeSolidIcon';
import { EdgeStartIcon } from '@/components/icons/EdgeStartIcon';
import { Family01Icon } from '@/components/icons/Family01Icon';
import { File02Icon } from '@/components/icons/File02Icon';
import { HeartIcon } from '@/components/icons/HeartIcon';
import { Home03Icon } from '@/components/icons/Home03Icon';
import { TileIcon } from '@/components/icons/TileIcon';
import { TriangleIcon } from '@/components/icons/TriangleIcon';
import { User01Icon } from '@/components/icons/User01Icon';
import { UserCircleIcon } from '@/components/icons/UserCircleIcon';

import {
  type ArrowEdgeRoute,
  type ArrowEdgeStyleVariant,
  DiagramEdgeArrowheads,
  DiagramEdgeLineStyle,
  DiagramEdgeRoute,
  DiagramNodeDisplay,
  DiagramNodeFill,
  DiagramNodeIcon,
  DiagramNodeShape,
  GraphVizIconShape,
  ICON_SHAPE_BY_NODE_SHAPE,
} from '../../diagramStyle';
import { type DiagramPickerOption } from './DiagramOptionPicker';
import {
  DiagramLineGlyph,
  DiagramShapeGlyph,
  GLYPH_SIZE,
} from './diagramStyleGlyphs';

/*
 * The option lists of the diagram style pickers. Give one to
 * `DiagramOptionPicker` as its `options`.
 */

export const LINE_ROUTE_OPTIONS: readonly DiagramPickerOption<ArrowEdgeRoute>[] =
  [
    { value: DiagramEdgeRoute.Elbow, label: 'Elbow' },
    { value: DiagramEdgeRoute.Straight, label: 'Straight' },
    { value: DiagramEdgeRoute.Curved, label: 'Curved' },
  ].map((option) => ({
    ...option,
    icon: <DiagramLineGlyph route={option.value} />,
  }));

export const LINE_PATTERN_OPTIONS: readonly DiagramPickerOption<ArrowEdgeStyleVariant>[] =
  [
    { value: DiagramEdgeLineStyle.Solid, label: 'Solid', Icon: EdgeSolidIcon },
    {
      value: DiagramEdgeLineStyle.Dashed,
      label: 'Dashed',
      Icon: EdgeDashedIcon,
    },
    {
      value: DiagramEdgeLineStyle.Dotted,
      label: 'Dotted',
      Icon: EdgeDottedIcon,
    },
  ].map(({ Icon, ...option }) => ({
    ...option,
    icon: <Icon size={GLYPH_SIZE} aria-hidden />,
  }));

/** Line widths in canvas pixels. */
export const LINE_WEIGHT_OPTIONS: readonly DiagramPickerOption<number>[] = [
  1, 2, 3,
].map((weight) => ({
  value: weight,
  label: `${weight}px`,
  icon: <DiagramLineGlyph weight={weight} />,
}));

/** The arrowheads at the source (start) and target (end) of a line. */
export const ARROWHEAD_OPTIONS: readonly DiagramPickerOption<DiagramEdgeArrowheads>[] =
  [
    { value: DiagramEdgeArrowheads.None, label: 'None', Icon: EdgeNoneIcon },
    { value: DiagramEdgeArrowheads.End, label: 'End', Icon: EdgeEndIcon },
    {
      value: DiagramEdgeArrowheads.Start,
      label: 'Start',
      Icon: EdgeStartIcon,
    },
    { value: DiagramEdgeArrowheads.Both, label: 'Both', Icon: EdgeBothIcon },
  ].map(({ Icon, ...option }) => ({
    ...option,
    icon: <Icon size={GLYPH_SIZE} aria-hidden />,
  }));

/** How a node draws: as a tile, as an icon above its title, or as a shape. */
export const NODE_DISPLAY_OPTIONS: readonly DiagramPickerOption<DiagramNodeDisplay>[] =
  [
    { value: DiagramNodeDisplay.Tile, label: 'Tile', Icon: TileIcon },
    { value: DiagramNodeDisplay.TileIcon, label: 'Icon', Icon: UserCircleIcon },
    { value: DiagramNodeDisplay.Shape, label: 'Shape', Icon: TriangleIcon },
  ].map(({ Icon, ...option }) => ({
    ...option,
    icon: <Icon size={GLYPH_SIZE} />,
  }));

/** The glyph of an icon node. People is the family icon. */
export const NODE_ICON_OPTIONS: readonly DiagramPickerOption<DiagramNodeIcon>[] =
  [
    { value: DiagramNodeIcon.House, label: 'House', Icon: Home03Icon },
    { value: DiagramNodeIcon.Person, label: 'Person', Icon: User01Icon },
    { value: DiagramNodeIcon.Heart, label: 'Heart', Icon: HeartIcon },
    {
      value: DiagramNodeIcon.Currency,
      label: 'Currency',
      Icon: CurrencyDollarCircleIcon,
    },
    { value: DiagramNodeIcon.Document, label: 'Document', Icon: File02Icon },
    { value: DiagramNodeIcon.Family, label: 'People', Icon: Family01Icon },
    { value: DiagramNodeIcon.Charity, label: 'Charity', Icon: CoinsHandIcon },
  ].map(({ Icon, ...option }) => ({
    ...option,
    icon: <Icon size={GLYPH_SIZE} />,
  }));

/** The background shape of the glyph of an icon node. */
export const ICON_SHAPE_OPTIONS: readonly DiagramPickerOption<GraphVizIconShape>[] =
  [
    { value: GraphVizIconShape.Circle, label: 'Circle' },
    { value: GraphVizIconShape.Triangle, label: 'Triangle' },
    { value: GraphVizIconShape.Diamond, label: 'Diamond' },
    { value: GraphVizIconShape.Square, label: 'Square' },
  ].map((option) => ({
    ...option,
    icon: <DiagramShapeGlyph shape={option.value} />,
  }));

/** The shape of a node drawn as a shape. */
export const NODE_SHAPE_OPTIONS: readonly DiagramPickerOption<DiagramNodeShape>[] =
  [
    { value: DiagramNodeShape.Circle, label: 'Circle' },
    { value: DiagramNodeShape.Triangle, label: 'Triangle' },
    { value: DiagramNodeShape.Diamond, label: 'Diamond' },
    { value: DiagramNodeShape.Square, label: 'Square' },
    { value: DiagramNodeShape.Rounded, label: 'Rectangle' },
  ].map((option) => ({
    ...option,
    icon: <DiagramShapeGlyph shape={ICON_SHAPE_BY_NODE_SHAPE[option.value]} />,
  }));

/** The body of a node drawn as a shape. */
export const NODE_FILL_OPTIONS: readonly DiagramPickerOption<DiagramNodeFill>[] =
  [
    { value: DiagramNodeFill.Filled, label: 'Filled' },
    { value: DiagramNodeFill.Outlined, label: 'Outlined' },
  ];
